/**
 * Lightweight, provider-agnostic error reporting.
 *
 * Goals for a v1 production rollout without committing to a vendor SDK yet:
 *   - One choke point (`reportError`) that the ErrorBoundary and global
 *     handlers funnel into.
 *   - Always log to the console so nothing is lost locally.
 *   - Optionally POST a compact JSON payload to a collector endpoint
 *     (VITE_ERROR_REPORT_URL) — best-effort, never blocks or throws.
 *   - Capture otherwise-invisible failures: uncaught errors and unhandled
 *     promise rejections (e.g. a rejected fetch with no .catch).
 *
 * Swapping in Sentry later is a drop-in: install @sentry/react, call
 * Sentry.init() in initErrorReporting(), and forward `reportError` to
 * Sentry.captureException — the call sites don't change.
 *
 * NB: never include PHI or tokens in reports. We send message + stack +
 * route only.
 */

const ENDPOINT = import.meta.env.VITE_ERROR_REPORT_URL || ''
const APP_ENV = import.meta.env.VITE_APP_ENV || 'local'

function buildPayload(error, context) {
  const err = error instanceof Error ? error : new Error(String(error))
  return {
    message: err.message,
    stack: err.stack || null,
    componentStack: context?.componentStack || null,
    kind: context?.kind || 'error',
    // Path only — never query strings, which can carry tokens/ids.
    path: typeof location !== 'undefined' ? location.pathname : null,
    appEnv: APP_ENV,
    ts: new Date().toISOString(),
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
  }
}

/**
 * Report an error. Safe to call from anywhere; never throws.
 */
export function reportError(error, context = {}) {
  const payload = buildPayload(error, context)

  // Always surface locally.
  console.error('[icer]', payload.kind, payload.message, error, context?.componentStack || '')

  if (!ENDPOINT) return
  try {
    const body = JSON.stringify(payload)
    // sendBeacon survives a page unload and doesn't block; fall back to a
    // fire-and-forget fetch when it's unavailable or refuses the payload.
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      const ok = navigator.sendBeacon(ENDPOINT, new Blob([body], { type: 'application/json' }))
      if (ok) return
    }
    if (typeof fetch === 'function') {
      fetch(ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        keepalive: true,
      }).catch(() => {})
    }
  } catch {
    /* reporting must never break the app */
  }
}

let installed = false

/**
 * Install global handlers + expose the hook the ErrorBoundary calls. Call
 * once, as early as possible, from main.jsx.
 */
export function initErrorReporting() {
  if (installed || typeof window === 'undefined') return
  installed = true

  // The ErrorBoundary looks for this hook (window.__icerReportError).
  window.__icerReportError = (error, context) => reportError(error, context)

  window.addEventListener('error', (e) => {
    reportError(e.error || e.message, { kind: 'window.error' })
  })

  window.addEventListener('unhandledrejection', (e) => {
    reportError(e.reason || 'Unhandled promise rejection', { kind: 'unhandledrejection' })
  })
}
